import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from './theme';

export const colorPalettes = [
    { name: 'Azure Pulse',    slug: 'azure',   description: 'Vibrant blues with electric highlights', preview: ['#2563EB', '#0EA5E9', '#22D3EE'] },
    { name: 'Emerald Bloom',  slug: 'emerald', description: 'Fresh greens with teal accents',         preview: ['#059669', '#14B8A6', '#6EE7B7'] },
    { name: 'Sunset Glow',    slug: 'sunset',  description: 'Warm oranges with rosy pops',            preview: ['#F97316', '#F43F5E', '#FACC15'] },
    { name: 'Violet Dream',   slug: 'violet',  description: 'Rich violets with magenta flares',       preview: ['#7C3AED', '#A855F7', '#EC4899'] },
    { name: 'Ruby Nova',      slug: 'ruby',    description: 'Bold reds with molten gold',             preview: ['#BE123C', '#F97316', '#FDE047'] },
    { name: 'Steel Wave',     slug: 'steel',   description: 'Modern slate with icy blues',            preview: ['#0F172A', '#475569', '#38BDF8'] },
    { name: 'Citrus Pop',     slug: 'citrus',  description: 'Playful lime with punchy pink',          preview: ['#FACC15', '#22C55E', '#FB7185'] },
];

const THEME_KEY   = 'theme';
const PALETTE_KEY = 'appColorPalette';

const ThemeContext = createContext(null);

export const AppThemeProvider = ({ children }) => {
    // Initialise from localStorage; fall back to OS preference on first visit
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const stored = localStorage.getItem(THEME_KEY);
        if (stored) return stored === 'dark';
        return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
    });

    const [selectedPalette, setSelectedPalette] = useState(() => {
        const stored = localStorage.getItem(PALETTE_KEY);
        return colorPalettes.some(p => p.slug === stored) ? stored : colorPalettes[0].slug;
    });

    // Sync body.dark class and localStorage whenever isDarkMode changes
    useEffect(() => {
        document.body.classList.toggle('dark', isDarkMode);
        localStorage.setItem(THEME_KEY, isDarkMode ? 'dark' : 'light');
    }, [isDarkMode]);

    // Apply palette CSS classes to both :root and body, and persist
    const applyPalette = useCallback((slug) => {
        const palette = colorPalettes.find(p => p.slug === slug) ?? colorPalettes[0];
        const root = document.documentElement;
        colorPalettes.forEach(p => {
            root.classList.remove(`theme-${p.slug}`);
            document.body.classList.remove(`theme-${p.slug}`);
        });
        root.classList.add(`theme-${palette.slug}`);
        document.body.classList.add(`theme-${palette.slug}`);
        localStorage.setItem(PALETTE_KEY, palette.slug);
    }, []);

    useEffect(() => {
        applyPalette(selectedPalette);
    }, [applyPalette, selectedPalette]);

    const toggleTheme = useCallback(() => setIsDarkMode(prev => !prev), []);

    const handlePaletteChange = useCallback((slug) => setSelectedPalette(slug), []);

    // Recreate MUI theme whenever dark/light mode changes
    const muiTheme = useMemo(
        () => createAppTheme(isDarkMode ? 'dark' : 'light'),
        [isDarkMode]
    );

    const value = useMemo(() => ({
        isDarkMode,
        toggleTheme,
        selectedPalette,
        handlePaletteChange,
        colorPalettes,
    }), [isDarkMode, toggleTheme, selectedPalette, handlePaletteChange]);

    return (
        <ThemeContext.Provider value={value}>
            <MuiThemeProvider theme={muiTheme}>
                <CssBaseline />
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};

export const useAppTheme = () => {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error('useAppTheme must be used within AppThemeProvider');
    return ctx;
};
